<template>
  <div class="constructor-page">
    <div class="constructor-layout">
      <!-- рабочая область / workspace -->
      <div class="constructor-main">
        <div
          ref="workspace"
          class="constructor-workspace"
        >
          <!-- заглушка для пустого списка рабочих объектов / empty state -->
          <div
            v-if="workItems.length === 0"
            class="constructor-empty"
          >
            <feather-icon
              icon="TerminalIcon"
              size="48"
              class="mb-1 text-muted"
            />
            <p class="text-muted">
              Выберите объект(ы) для работы с помощью команды <code>/obj имя_объекта</code> или создайте новый с помощью <code>/create тип имя</code>.
            </p>
          </div>

          <!-- список рабочих объектов -->
          <template v-for="(item, index) in workItems">
            <!-- карточка объекта -->
            <cbs-constructor-object-card
              v-if="item.objectId"
              :ref="'objCard_' + index"
              :key="'obj-' + index"
              :item="item"
              :active="activeWorkItemIndex === index"
              :all-entities="allEntities"
              :entities-loading="entitiesLoading"
              :mount-operands="mountOperands"
              :all-objects="allObjects"
              :object-fields-cache="objectFieldsCache"
              @activate="activateWorkItem(index)"
              @toggle-collapse="toggleCollapse(index)"
              @remove="removeWorkItem(index, item)"
              @toggle-expand="toggleItemExpand(index)"
              @toggle-attrs="toggleItemAttrs(index)"
              @ensure-entities="ensureEntitiesLoaded"
              @add-attr="entity => addAttrToObject(index, entity)"
              @update-attr="payload => updateAttrField(index, payload)"
              @toggle-delete-attr="payload => toggleDeleteAttr(index, payload)"
              @remove-attr="payload => removeAttr(index, payload)"
              @load-steps="payload => loadMountSteps(payload)"
              @update-step="payload => updateStepField(index, payload)"
              @toggle-delete-step="payload => toggleDeleteStep(payload)"
              @ensure-operands="loadMountOperands"
              @load-object-fields="loadObjectFields"
              @add-step="addMountStep(index)"
              @rename="name => renameObject(index, name)"
              @add-work-object="objectId => addWorkObject(objectId)"
            />

            <!-- карточка связки -->
            <cbs-constructor-join-card
              v-else-if="item.joinId"
              :key="'join-' + index"
              :item="item"
              :active="activeWorkItemIndex === index"
              :all-objects="allObjects"
              :object-fields-cache="objectFieldsCache"
              @activate="activateWorkItem(index)"
              @toggle-collapse="toggleCollapse(index)"
              @remove="removeWorkItem(index, item)"
              @update-field="payload => updateJoinField(index, payload)"
              @load-object-fields="loadObjectFields"
              @add-work-object="objectId => addWorkObject(objectId)"
            />

            <!-- карточка формулы -->
            <cbs-constructor-formula-card
              v-else-if="item.formulaId"
              :key="'formula-' + index"
              :item="item"
              :active="activeWorkItemIndex === index"
              @activate="activateWorkItem(index)"
              @toggle-collapse="toggleCollapse(index)"
              @remove="removeWorkItem(index, item)"
              @update-field="payload => updateFormulaField(index, payload)"
            />

            <div
              v-else
              :key="'cmd-' + index"
              class="constructor-work-item"
              :class="{ 'constructor-work-item--active': activeWorkItemIndex === index }"
              @click="activeWorkItemIndex = index"
            >
              <div class="constructor-work-item-header">
                <span
                  class="constructor-work-item-toggle"
                  title="Свернуть / Развернуть"
                  @click.stop="toggleCollapse(index)"
                >
                  <feather-icon
                    :icon="item.collapsed ? 'ChevronRightIcon' : 'ChevronDownIcon'"
                    size="14"
                  />
                </span>
                <span class="constructor-work-item-cmd">{{ item.command }}</span>
              </div>
              <template v-if="!item.collapsed">
                {{ item }}
                <div
                  v-if="item.format === 'markdown'"
                  class="constructor-work-item-body constructor-markdown"
                  v-html="renderMarkdown(item.text)"
                />
                <div
                  v-else
                  class="constructor-work-item-body"
                >
                  {{ item.text }}
                </div>
              </template>
            </div>
          </template>
        </div>
        <!-- context and status -->
        <div
          v-if="contextObject || contextEntity"
          class="constructor-statusbar"
        >
          <feather-icon
            icon="BoxIcon"
            size="14"
            class="constructor-statusbar-icon"
          />
          <span class="constructor-statusbar-label">Контекст:</span>
          <template v-if="contextObject">
            <span class="constructor-statusbar-object">{{ contextObject.objectName }}</span>
            <span class="constructor-statusbar-id">#{{ contextObject.objectId }}</span>
          </template>
          <template v-if="contextEntity">
            <feather-icon
              icon="ChevronRightIcon"
              size="14"
              class="constructor-statusbar-separator"
            />
            <span class="constructor-statusbar-entity">{{ contextEntity.entityName }}</span>
            <span class="constructor-statusbar-id">#{{ contextEntity.entityId }}</span>
          </template>
        </div>

        <!-- status message -->
        <div
          v-if="statusMessage"
          class="constructor-status-message"
          @click="statusMessage = null"
          v-html="renderMarkdown(statusMessage)"
        />

        <!-- command line -->
        <div class="constructor-input-area">
          <b-form
            class="constructor-form"
            @submit.prevent="sendMessage"
          >
            <b-form-input
              ref="cmdInput"
              v-model="inputText"
              placeholder="Введите команду..."
              autocomplete="off"
              @keydown.up="historyUp"
              @keydown.down="historyDown"
            />
            <b-button
              variant="primary"
              type="submit"
              :disabled="!inputText.trim()"
            >
              <feather-icon
                icon="SendIcon"
                size="16"
              />
            </b-button>
            <b-button
              variant="outline-primary"
              title="Мастер бюджетной цепочки"
              @click="openChainWizard"
            >
              <feather-icon
                icon="GitBranchIcon"
                size="16"
              />
            </b-button>
          </b-form>
        </div>
      </div>

      <div
        class="constructor-divider"
        @mousedown="startResize"
      />

      <!-- обозреватель объектов / properties panel -->
      <div
        class="constructor-props"
        :style="{ width: propsWidth + 'px', minWidth: propsWidth + 'px' }"
      >
        <h6 class="constructor-props-title">
          Properties
        </h6>
        <template v-if="contextObject && objectProps">
          <div class="constructor-props-section">
            <span class="constructor-props-section-title">Object</span>
          </div>
          <div
            v-for="(val, key) in objectProps"
            :key="key"
            class="constructor-props-row"
          >
            <span class="constructor-props-key">{{ key }}</span>
            <span class="constructor-props-val">{{ val }}</span>
          </div>
        </template>
        <template v-else-if="contextEntity && entityProps">
          <div class="constructor-props-section">
            <span class="constructor-props-section-title">Attribute</span>
          </div>
          <div
            v-for="(val, key) in entityProps"
            :key="key"
            class="constructor-props-row"
          >
            <span class="constructor-props-key">{{ key }}</span>
            <span class="constructor-props-val">{{ val }}</span>
          </div>
        </template>
        <template v-else>
          <div
            class="constructor-tree"
            tabindex="0"
            @keydown="onTreeKeydown"
          >
            <!-- корневой узел домена -->
            <div
              class="constructor-tree-root"
              :class="{ 'constructor-tree-focused': focusedTreeIndex === 0 }"
              @click="onTreeNodeClick(0); domainExpanded = !domainExpanded"
            >
              <feather-icon
                :icon="domainExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="14"
                class="constructor-tree-toggle"
              />
              <feather-icon
                icon="DatabaseIcon"
                size="14"
                class="constructor-tree-icon"
              />
              <span class="constructor-tree-domain">{{ domainName }}</span>
            </div>

            <!-- дерево мета-объектов и объектов -->
            <template v-if="domainExpanded">
              <!-- поиск объектов в дереве -->
              <b-form-input
                v-model="treeSearch"
                size="sm"
                class="constructor-tree-search"
                placeholder="Поиск объекта..."
                autocomplete="off"
                @click.native.stop
                @input="onTreeSearchInput"
                @keydown.down.prevent="focusFirstTreeObject"
              />
              <div
                v-if="treeLoading"
                class="constructor-tree-loading"
              >
                <b-spinner
                  small
                  variant="primary"
                />
                <span class="ml-1 text-muted small">Загрузка...</span>
              </div>
              <div v-else>
                <div
                  v-for="meta in filteredMetaTree"
                  :key="meta.sid"
                  class="constructor-tree-meta"
                >
                  <div
                    class="constructor-tree-meta-header"
                    :class="{ 'constructor-tree-focused': focusedTreeIndex === getMetaNodeIndex(meta.sid) }"
                    @click="onTreeNodeClick(getMetaNodeIndex(meta.sid)); toggleMeta(meta.sid)"
                  >
                    <feather-icon
                      :icon="expandedMetas[meta.sid] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                      size="14"
                      class="constructor-tree-toggle"
                    />
                    <feather-icon
                      icon="FolderIcon"
                      size="14"
                      class="constructor-tree-icon"
                    />
                    <span class="constructor-tree-meta-name">{{ meta.name }}</span>
                    <span class="constructor-tree-meta-count">{{ meta.objects.length }}</span>
                  </div>
                  <div
                    v-if="expandedMetas[meta.sid]"
                    class="constructor-tree-objects"
                  >
                    <div
                      v-for="obj in meta.objects"
                      :key="obj.id"
                      class="constructor-tree-object"
                      :class="{ 'constructor-tree-focused': focusedTreeIndex === getObjectNodeIndex(meta.sid, obj.id) }"
                      @click="onTreeNodeClick(getObjectNodeIndex(meta.sid, obj.id))"
                    >
                      <b-form-checkbox
                        :checked="isObjectSelected(obj.id)"
                        @change="toggleObjectSelection(obj)"
                      />
                      <span
                        class="constructor-tree-object-name"
                        :title="obj.sid"
                      >
                        {{ obj.name }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Связки (Joins) folder -->
                <div class="constructor-tree-meta">
                  <div
                    class="constructor-tree-meta-header"
                    @click="toggleJoins"
                  >
                    <feather-icon
                      :icon="joinsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                      size="14"
                      class="constructor-tree-toggle"
                    />
                    <feather-icon
                      icon="GitMergeIcon"
                      size="14"
                      class="constructor-tree-icon"
                    />
                    <span class="constructor-tree-meta-name">Связки</span>
                    <span
                      v-if="joins.length"
                      class="constructor-tree-meta-count"
                    >{{ filteredJoins.length }}</span>
                    <b-spinner
                      v-if="joinsLoading"
                      small
                      variant="primary"
                      class="ml-1"
                    />
                  </div>
                  <div
                    v-if="joinsExpanded"
                    class="constructor-tree-objects"
                  >
                    <div
                      v-for="join in filteredJoins"
                      :key="join.id"
                      class="constructor-tree-object"
                    >
                      <b-form-checkbox
                        :checked="isJoinSelected(join.id)"
                        @change="toggleJoinSelection(join)"
                      />
                      <span
                        class="constructor-tree-object-name"
                        :title="join.sid"
                      >
                        {{ join.name || join.sid || `#${join.id}` }}
                      </span>
                    </div>
                    <div
                      v-if="!joinsLoading && filteredJoins.length === 0"
                      class="constructor-tree-empty"
                    >
                      Не найдено
                    </div>
                  </div>
                </div>

                <!-- Формулы (Formulas) folder -->
                <div class="constructor-tree-meta">
                  <!-- папка формул -->
                  <div
                    class="constructor-tree-meta-header"
                    @click="toggleFormulas"
                  >
                    <feather-icon
                      :icon="formulasExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                      size="14"
                      class="constructor-tree-toggle"
                    />
                    <feather-icon
                      icon="HashIcon"
                      size="14"
                      class="constructor-tree-icon"
                    />
                    <span class="constructor-tree-meta-name">Формулы</span>
                    <span
                      v-if="formulas.length"
                      class="constructor-tree-meta-count"
                    >{{ filteredFormulas.length }}</span>
                    <b-spinner
                      v-if="formulasLoading"
                      small
                      variant="primary"
                      class="ml-1"
                    />
                  </div>
                  <!-- список формул -->
                  <div
                    v-if="formulasExpanded"
                    class="constructor-tree-objects"
                  >
                    <div
                      v-for="formula in filteredFormulas"
                      :key="formula.id"
                      class="constructor-tree-object"
                    >
                      <b-form-checkbox
                        :checked="isFormulaSelected(formula.id)"
                        @change="toggleFormulaSelection(formula)"
                      />
                      <span
                        class="constructor-tree-object-name"
                        :title="formula.sid"
                      >
                        {{ formula.name || formula.sid || `#${formula.id}` }}
                      </span>
                    </div>
                    <div
                      v-if="!formulasLoading && filteredFormulas.length === 0"
                      class="constructor-tree-empty"
                    >
                      Не найдено
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- Chain Wizard -->
    <cbs-constructor-chain-wizard
      :visible.sync="chainWizardVisible"
      :all-entities="allEntities"
      :ws="ws"
      :get-token="getToken"
      :mount-operands="mountOperands"
      :all-objects="allObjects"
      :edit-chain-id="editChainId"
      @wizard-active="v => wizardActive = v"
      @chain-created="onChainCreated"
      @chain-updated="onChainUpdated"
    />

    <!-- Export modal -->
    <b-modal
      v-model="exportModalVisible"
      title="Export Structure JSON"
      size="xl"
      ok-only
      ok-title="Закрыть"
      scrollable
      centered
    >
      <div
        v-if="exportLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
        <p class="mt-1 text-muted">
          Загрузка структуры объектов...
        </p>
      </div>
      <template v-else>
        <div class="d-flex justify-content-end mb-1">
          <b-button
            size="sm"
            :variant="exportCopied ? 'success' : 'outline-primary'"
            @click="copyExportJson"
          >
            <feather-icon
              :icon="exportCopied ? 'CheckIcon' : 'CopyIcon'"
              size="14"
              class="mr-25"
            />
            {{ exportCopied ? 'Скопировано!' : 'Копировать' }}
          </b-button>
        </div>
        <textarea
          ref="exportTextarea"
          :value="exportJson"
          class="form-control"
          readonly
          rows="25"
          style="font-family: monospace; font-size: 12px; white-space: pre; overflow-x: auto;"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BForm, BFormInput, BButton, BFormCheckbox, BSpinner, BModal,
} from 'bootstrap-vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import useCubus from '@/cubus/services/useCubus'
import useJwt from '@/cubus/jwt/useJwt'
import { getConfig } from '@/libs/axios'
import CbsDebug from '@/cubus/components/debug/CbsDebug.vue'
import { parseCommand, HELP_TEXT } from '@/cubus/services/chatCommands'
import CbsConstructorObjectCard from './CbsConstructorObjectCard.vue'
import CbsConstructorChainWizard from './CbsConstructorChainWizard.vue'
import CbsConstructorJoinCard from './CbsConstructorJoinCard.vue'
import CbsConstructorFormulaCard from './CbsConstructorFormulaCard.vue'

const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_INTERVAL = 3000

export default {
  name: 'CbsConstructor',
  components: {
    CbsDebug,
    CbsConstructorObjectCard,
    CbsConstructorChainWizard,
    CbsConstructorJoinCard,
    CbsConstructorFormulaCard,
    BForm,
    BFormInput,
    BButton,
    BFormCheckbox,
    BSpinner,
    BModal,
  },
  data() {
    return {
      inputText: '',
      workItems: [],
      connectionStatus: 'connecting',
      ws: null,
      reconnectAttempts: 0,
      reconnectTimer: null,
      commandHistory: [],
      historyIndex: -1,
      contextObject: null,
      contextEntity: null,
      objectProps: null,
      entityProps: null,
      allObjects: [],
      treeLoading: false,
      expandedMetas: {},
      selectedObjects: {},
      domainExpanded: true,
      allEntities: [],
      entitiesLoading: false,
      mountOperands: [],
      objectFieldsCache: {},
      focusedTreeIndex: -1,
      activeWorkItemIndex: -1,
      treeSearch: '',
      statusMessage: null,
      joinsExpanded: false,
      joins: [],
      joinsLoading: false,
      selectedJoins: {},
      formulasExpanded: false,
      formulas: [],
      formulasLoading: false,
      selectedFormulas: {},
      propsWidth: 300,
      resizeStartX: 0,
      exportModalVisible: false,
      exportJson: '',
      exportLoading: false,
      exportCopied: false,
      resizeStartWidth: 0,
      chainWizardVisible: false,
      wizardActive: false,
      editChainId: null,
    }
  },
  computed: {
    domainSid() {
      const session = useCubus.getSession()
      return (session && session.domain && session.domain.sid) || ''
    },
    domainName() {
      const session = useCubus.getSession()
      if (session && session.domain) {
        return session.domain.name || session.domain.sid || ''
      }
      return ''
    },
    metaTree() {
      const groups = {}
      this.allObjects.forEach(obj => {
        const metaSid = obj.meta || 'unknown'
        const metaName = obj.meta_name || 'Unknown'
        if (!groups[metaSid]) {
          groups[metaSid] = { sid: metaSid, name: metaName, objects: [] }
        }
        groups[metaSid].objects.push(obj)
      })
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredMetaTree() {
      if (!this.treeSearch.trim()) return this.metaTree
      const parts = this.treeSearch.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      const result = []
      this.metaTree.forEach(meta => {
        const filtered = meta.objects.filter(obj => re.test((obj.name || '').toLowerCase()) || re.test((obj.sid || '').toLowerCase()))
        if (filtered.length) {
          result.push({ ...meta, objects: filtered })
        }
      })
      return result
    },
    flatTreeNodes() {
      const nodes = []
      nodes.push({ type: 'domain' })
      if (this.domainExpanded) {
        this.filteredMetaTree.forEach(meta => {
          nodes.push({ type: 'meta', sid: meta.sid })
          if (this.expandedMetas[meta.sid]) {
            meta.objects.forEach(obj => {
              nodes.push({ type: 'object', metaSid: meta.sid, obj })
            })
          }
        })
      }
      return nodes
    },
    hasSelectedObjects() {
      return Object.keys(this.selectedObjects).length > 0
    },
    filteredJoins() {
      if (!this.treeSearch.trim()) return this.joins
      const parts = this.treeSearch.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      return this.joins.filter(j => re.test((j.name || '').toLowerCase()) || re.test((j.sid || '').toLowerCase()))
    },
    filteredFormulas() {
      if (!this.treeSearch.trim()) return this.formulas
      const parts = this.treeSearch.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      return this.formulas.filter(f => re.test((f.name || '').toLowerCase()) || re.test((f.sid || '').toLowerCase()))
    },
  },
  created() {
    useCubus.setPageTitle('Constructor')
  },
  mounted() {
    this.connectWebSocket()
    this.focusInput()
    this.loadAllObjects()
  },
  beforeDestroy() {
    this.closeWebSocket()
  },
  methods: {
    getWsUrl() {
      const baseURL = getConfig()
      const url = new URL('/ws/chat', baseURL)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return url.toString()
    },
    getToken() {
      // PROP-086: токен из Vuex-сессии (в памяти), а не из localStorage. Передаётся пропом в
      // CbsConstructorChainWizard и уходит в AI-сервис на другом origin, куда cookie не приложится.
      const s = this.$store.getters['cubus-store/SESSION']
      return (s && s.token) || ''
    },
    connectWebSocket() {
      this.closeWebSocket()
      this.connectionStatus = 'connecting'
      const url = this.getWsUrl()
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.connectionStatus = 'online'
        this.reconnectAttempts = 0
        this.ws.send(JSON.stringify({
          type: 'auth',
          token: this.getToken(),
        }))
      }

      this.ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'message') {
            // When wizard is active, let it handle WS responses
            if (this.wizardActive) return
            if (data.context) {
              this.updateContext(data.context)
            }
            if (data.properties) {
              this.updateProperties(data.properties)
            }
            if (this.pendingCommand) {
              if (this.pendingCommand.startsWith('__add_attr_')) {
                const idx = parseInt(this.pendingCommand.replace('__add_attr_', ''), 10)
                const item = this.workItems[idx]
                if (item && item.objectId) {
                  this.loadObjectFull(idx, item.objectId)
                }
              } else if (this.pendingCommand.startsWith('__create_formula_')) {
                const idx = parseInt(this.pendingCommand.replace('__create_formula_', ''), 10)
                const toolItem = this.workItems[idx]
                if (toolItem) {
                  const isError = data.text && (data.text.includes('Ошибка') || data.text.includes('ошибка'))
                  const prefix = isError ? '❌ ' : (data.text && data.text.includes('уже существует') ? '⚠️ ' : '✅ ')
                  this.$set(this.workItems, idx, { ...toolItem, text: prefix + data.text })
                }
                this.loadFormulas().then(() => { this.formulasExpanded = true })
              } else if (data.context && data.context.objectId) {
                if (this.pendingCommand.startsWith('/create ')) {
                  this.onObjectCreated(data.context)
                } else if (this.pendingCommand.startsWith('/obj ') || this.pendingCommand.startsWith('/sel ')) {
                  this.onObjectSelected(data.context)
                }
              }
            }
            this.showStatus(data.text)
            this.pendingCommand = null
            this.scrollToBottom()
          } else if (data.type === 'error') {
            this.showStatus(`Ошибка: ${data.text}`)
            this.pendingCommand = null
          }
        } catch {
          // ignore non-JSON
        }
      }

      this.ws.onclose = () => {
        this.connectionStatus = 'offline'
        this.scheduleReconnect()
      }

      this.ws.onerror = () => {
        this.connectionStatus = 'offline'
      }
    },
    closeWebSocket() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.close()
        this.ws = null
      }
    },
    scheduleReconnect() {
      if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return
      this.reconnectAttempts += 1
      this.reconnectTimer = setTimeout(() => {
        this.connectWebSocket()
      }, RECONNECT_INTERVAL)
    },
    updateContext(ctx) {
      if (ctx.objectId) {
        this.contextObject = { objectId: ctx.objectId, objectName: ctx.objectName }
      } else {
        this.contextObject = null
        this.objectProps = null
      }
      if (ctx.entityId) {
        this.contextEntity = { entityId: ctx.entityId, entityName: ctx.entityName }
      } else {
        this.contextEntity = null
        this.entityProps = null
      }
    },
    updateProperties(props) {
      if (props.type === 'object') {
        this.objectProps = {
          Name: props.name || '',
          SID: props.sid || '',
          Meta: props.meta || '',
          Table: props.tablename || '',
          Description: props.descr || '',
        }
        this.entityProps = null
      } else if (props.type === 'entity') {
        this.entityProps = {
          Name: props.name || '',
          SID: props.sid || '',
          Datatype: props.datatype || '',
          Required: props.isrequired ? 'Yes' : 'No',
          Unique: props.isunique ? 'Yes' : 'No',
        }
      }
    },
    showStatus(text) {
      this.statusMessage = text
    },
    handleLocalCommand(text) {
      // Handle /chain_edit <chainId> command (not in parseCommand)
      if (text.trim().toLowerCase().startsWith('/chain_edit')) {
        const arg = text.trim().substring('/chain_edit'.length).trim()
        if (arg) {
          this.openChainWizardForEdit(arg)
        } else {
          this.showStatus('Использование: /chain_edit <chain_id UUID>')
        }
        return true
      }

      const result = parseCommand(text)
      if (!result.handled) return false

      if (result.localReply === 'help') {
        this.showStatus(HELP_TEXT)
        return true
      }
      if (result.localReply === 'clear') {
        this.workItems = []
        this.contextObject = null
        this.contextEntity = null
        this.objectProps = null
        this.entityProps = null
        this.selectedObjects = {}
        this.scrollToBottom()
        return true
      }
      if (result.localReply === 'export') {
        this.runExport()
        return true
      }
      if (result.localReply) {
        this.showStatus(result.localReply)
        return true
      }
      if (result.serverText) {
        this.pendingCommand = result.pendingCommand
        this.sendToServer(result.serverText)
      }
      return true
    },
    sendMessage() {
      const text = this.inputText.trim()
      if (!text) return

      this.commandHistory.unshift(text)
      this.historyIndex = -1
      this.inputText = ''

      if (text.trim().toLowerCase().startsWith('/create join ')) {
        const rawName = text.trim().substring('/create join '.length).trim()
        if (!rawName) {
          this.showStatus('Укажите имя связки. Например: `/create join Моя связка`')
          return
        }
        const joinName = rawName.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        this.createJoin(joinName)
        return
      }

      if (text.trim().toLowerCase().startsWith('/create formula ')) {
        const rawName = text.trim().substring('/create formula '.length).trim()
        if (!rawName) {
          this.showStatus('Укажите имя формулы. Например: `/create formula Моя Формула`')
          return
        }
        const formulaName = rawName.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        const toolIndex = this.workItems.length
        this.workItems.push({
          command: `/create formula ${formulaName}`,
          text: `⚙ Создаю формулу **"${formulaName}"**...`,
          format: 'markdown',
          collapsed: false,
        })
        this.activeWorkItemIndex = toolIndex
        this.scrollToBottom()
        this.pendingCommand = `__create_formula_${toolIndex}`
        this.sendToServer(`/create formula ${formulaName}`)
        return
      }

      if (text.trim().toLowerCase() === '/mcp_tools') {
        this.pendingCommand = '/mcp_tools'
        this.sendToServer('/mcp_tools')
        return
      }

      if (text.trim().toLowerCase() === '/datatypes') {
        this.pendingCommand = '/datatypes'
        this.sendToServer('/datatypes')
        return
      }

      if (this.handleLocalCommand(text)) return

      this.pendingCommand = text
      this.sendToServer(text)
    },
    sendToServer(text) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.showStatus('Нет подключения к серверу. Попробуйте позже.')
        this.pendingCommand = null
        return
      }

      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text,
        token: this.getToken(),
      }))
    },
    renderMarkdown(text) {
      if (!text) return ''
      // BUG-820: marked пропускает сырой HTML насквозь, а результат идёт в v-html.
      return DOMPurify.sanitize(marked.parse(text))
    },
    historyUp(e) {
      if (this.commandHistory.length === 0) return
      if (this.historyIndex < this.commandHistory.length - 1) {
        e.preventDefault()
        this.historyIndex += 1
        this.inputText = this.commandHistory[this.historyIndex]
      }
    },
    historyDown(e) {
      if (this.historyIndex < 0) return
      e.preventDefault()
      if (this.historyIndex > 0) {
        this.historyIndex -= 1
        this.inputText = this.commandHistory[this.historyIndex]
      } else {
        this.historyIndex = -1
        this.inputText = ''
      }
    },
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.cmdInput
        if (input) input.focus()
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.workspace
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    loadAllObjects() {
      this.treeLoading = true
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'objects',
          param: { filter: 'all' },
        },
      })
        .then(response => {
          if (response.data.objects) {
            this.allObjects = response.data.objects
          }
        })
        .catch(() => {})
        .finally(() => {
          this.treeLoading = false
        })
    },
    startResize(e) {
      e.preventDefault()
      this.resizeStartX = e.clientX
      this.resizeStartWidth = this.propsWidth
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    onResize(e) {
      const delta = this.resizeStartX - e.clientX
      const newWidth = Math.max(150, Math.min(600, this.resizeStartWidth + delta))
      this.propsWidth = newWidth
    },
    stopResize() {
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },
    getMetaNodeIndex(metaSid) {
      return this.flatTreeNodes.findIndex(n => n.type === 'meta' && n.sid === metaSid)
    },
    getObjectNodeIndex(metaSid, objId) {
      return this.flatTreeNodes.findIndex(n => n.type === 'object' && n.metaSid === metaSid && n.obj.id === objId)
    },
    onTreeNodeClick(index) {
      this.focusedTreeIndex = index
    },
    scrollToFocusedTree() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.constructor-tree .constructor-tree-focused')
        if (el) {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    onTreeKeydown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      const { key } = e
      const nodes = this.flatTreeNodes
      if (nodes.length === 0) return

      if (key === 'ArrowDown') {
        e.preventDefault()
        if (this.focusedTreeIndex < nodes.length - 1) {
          this.focusedTreeIndex += 1
          this.scrollToFocusedTree()
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        if (this.focusedTreeIndex > 0) {
          this.focusedTreeIndex -= 1
          this.scrollToFocusedTree()
        }
      } else if (key === 'ArrowRight') {
        e.preventDefault()
        const node = nodes[this.focusedTreeIndex]
        if (node && node.type === 'domain' && !this.domainExpanded) {
          this.domainExpanded = true
        } else if (node && node.type === 'meta' && !this.expandedMetas[node.sid]) {
          this.$set(this.expandedMetas, node.sid, true)
        }
      } else if (key === 'ArrowLeft') {
        e.preventDefault()
        const node = nodes[this.focusedTreeIndex]
        if (node && node.type === 'domain' && this.domainExpanded) {
          this.domainExpanded = false
        } else if (node && node.type === 'meta' && this.expandedMetas[node.sid]) {
          this.$set(this.expandedMetas, node.sid, false)
        } else if (node && node.type === 'object') {
          // Jump to parent meta
          const parentIdx = this.getMetaNodeIndex(node.metaSid)
          if (parentIdx >= 0) {
            this.focusedTreeIndex = parentIdx
            this.scrollToFocusedTree()
          }
        } else if (node && node.type === 'meta') {
          // Jump to domain root
          this.focusedTreeIndex = 0
          this.scrollToFocusedTree()
        }
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault()
        const node = nodes[this.focusedTreeIndex]
        if (node && node.type === 'domain') {
          this.domainExpanded = !this.domainExpanded
        } else if (node && node.type === 'meta') {
          this.toggleMeta(node.sid)
        } else if (node && node.type === 'object') {
          this.toggleObjectSelection(node.obj)
        }
      }
    },
    toggleMeta(metaSid) {
      this.$set(this.expandedMetas, metaSid, !this.expandedMetas[metaSid])
    },
    onTreeSearchInput() {
      if (!this.treeSearch.trim()) return
      this.filteredMetaTree.forEach(meta => {
        if (meta.objects.length <= 10) {
          this.$set(this.expandedMetas, meta.sid, true)
        }
      })
    },
    focusFirstTreeObject() {
      const nodes = this.flatTreeNodes
      const idx = nodes.findIndex(n => n.type === 'object')
      if (idx >= 0) {
        this.focusedTreeIndex = idx
        this.scrollToFocusedTree()
        this.$el.querySelector('.constructor-tree').focus()
      }
    },
    isObjectSelected(objId) {
      return !!this.selectedObjects[objId]
    },
    ensureEntitiesLoaded() {
      if (this.allEntities.length === 0 && !this.entitiesLoading) {
        this.loadAllEntities()
      }
    },
    loadAllEntities() {
      this.entitiesLoading = true
      // Load entities and datatype reference in parallel
      const entitiesPromise = useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'query',
          mode: 'sync',
          param: {
            objectsid: 'entity',
            limit: 10000,
            offset: 0,
          },
        },
      })
      const datatypeRefPromise = useCubus.loadRef(this, { entitysid: 'datatype' }).catch(() => null)
      Promise.all([entitiesPromise, datatypeRefPromise])
        .then(([response, dtRef]) => {
          // Build datatype ID → sid map from reference table
          const dtMap = {}
          if (dtRef) {
            dtRef.forEach(r => { dtMap[r.value] = r.sid || r.title || '' })
          }
          if (response.data.result && response.data.result.query) {
            const { records } = response.data.result.query.recordset
            this.allEntities = (records || []).map(rec => {
              const cells = rec.cells || rec
              const dtCell = cells.datatype
              // Resolve datatype: try cell sid/title first, then map numeric ID via reference
              let datatype = ''
              if (dtCell) {
                datatype = dtCell.sid || dtCell.title || ''
                if (!datatype && dtCell.value && dtMap[dtCell.value]) {
                  datatype = dtMap[dtCell.value]
                }
              }
              return {
                id: cells.id ? cells.id.value : (cells[0] && cells[0].value),
                name: cells.name ? cells.name.value : (cells[1] && cells[1].value) || '',
                sid: cells.sid ? cells.sid.value : (cells[2] && cells[2].value) || '',
                datatype,
              }
            }).filter(e => e.id && e.name)
          }
        })
        .catch(() => {})
        .finally(() => {
          this.entitiesLoading = false
        })
    },
    addAttrToObject(index, entity) {
      const item = this.workItems[index]
      if (!item || !item.objectId) return

      this.pendingCommand = `__add_attr_${index}`
      this.sendToServer(`/add_attr ${item.objectId} ${entity.id}`)
    },
    updateAttrField(index, {
      attributeid, attr, field, value,
    }) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'saverecord',
          mode: 'sync',
          param: {
            objectsid: 'attribute',
            record: {
              id: { value: attributeid, status: 'loaded' },
              name: { value: attr.label, status: 'loaded' },
              objectid: { value: this.workItems[index].objectId, status: 'loaded' },
              entityid: { value: attr.entityid, status: 'loaded' },
              [field]: { value, status: 'changed' },
            },
          },
        },
      })
        .then(() => {
          this.loadObjectFull(index, this.workItems[index].objectId)
        })
        .catch(e => {
          console.error('Failed to update attribute:', e)
        })
    },
    loadAttrDelStatus(index, objectId) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'query',
          mode: 'sync',
          param: {
            objectsid: 'attribute',
            limit: 1000,
            offset: 0,
            filter: {
              node: {
                isactive: true,
                oper: { sid: 'and' },
                nodes: [],
                conds: [
                  {
                    isactive: true,
                    oper: { sid: 'equal' },
                    args: [
                      { type: { sid: 'entitysid' }, value: 'object' },
                      { type: { sid: 'value' }, value: String(objectId) },
                    ],
                  },
                  {
                    isactive: true,
                    oper: { sid: 'equal' },
                    args: [
                      { type: { sid: 'entitysid' }, value: 'del' },
                      { type: { sid: 'value' }, value: 'true' },
                    ],
                  },
                ],
              },
            },
          },
        },
      })
        .then(response => {
          if (!response.data.result || !response.data.result.query) return
          const { records } = response.data.result.query.recordset
          if (!records || records.length === 0) return
          const item = this.workItems[index]
          if (!item || !item.objectFull) return
          if (!item.objectFull.attributes) item.objectFull.attributes = []
          const existingIds = new Set(item.objectFull.attributes.map(a => a.attributeid))
          records.forEach(rec => {
            const c = rec.cells || rec
            const attrId = c.id ? c.id.value : null
            if (!attrId || existingIds.has(attrId)) return
            item.objectFull.attributes.push({
              attributeid: attrId,
              label: c.name ? c.name.value : '',
              key: c.key ? c.key.value : '',
              entitysid: c.entitysid ? c.entitysid.value : '',
              entityid: c.entityid ? c.entityid.value : null,
              datatype: c.datatype ? (c.datatype.title || c.datatype.value || '') : '',
              refobjectid: c.refobjectid ? c.refobjectid.value : null,
              isrequired: c.isrequired ? c.isrequired.value : false,
              isunique: c.isunique ? c.isunique.value : false,
              del: true,
            })
          })
        })
        .catch(e => {
          console.error('Failed to load attr del status:', e)
        })
    },
    attrRecord(index, attr) {
      return {
        id: { value: attr.attributeid, status: 'loaded' },
        name: { value: attr.label, status: 'loaded' },
        objectid: { value: this.workItems[index].objectId, status: 'loaded' },
        entityid: { value: attr.entityid, status: 'loaded' },
      }
    },
    toggleDeleteAttr(index, { attr }) {
      const newDel = !attr.del
      this.$set(attr, 'del', newDel)
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'saverecord',
          mode: 'sync',
          param: {
            objectsid: 'attribute',
            record: {
              ...this.attrRecord(index, attr),
              del: { value: newDel, status: 'changed' },
            },
          },
        },
      })
        .catch(e => {
          console.error('Failed to toggle delete attribute:', e)
          this.$set(attr, 'del', !newDel)
        })
    },
    removeAttr(index, { attr }) {
      const attrObj = this.allObjects.find(o => o.sid === 'attribute')
      const param = {
        objectsid: 'attribute',
        record: {
          ...this.attrRecord(index, attr),
          del: { value: true, status: 'loaded' },
        },
      }
      if (attrObj) param.objectid = attrObj.id
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'removerecord',
          param,
        },
      })
        .then(response => {
          if (response.data && response.data.thread) {
            this.delayThreadRemoveAttr(response.data.thread, index)
          } else {
            this.loadObjectFull(index, this.workItems[index].objectId)
          }
        })
        .catch(e => {
          console.error('Failed to remove attribute:', e)
        })
    },
    delayThreadRemoveAttr(threadName, index) {
      setTimeout(() => this.loadThreadRemoveAttr(threadName, index), 500)
    },
    loadThreadRemoveAttr(threadName, index) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'thread',
          param: { threadname: threadName },
        },
      })
        .then(response => {
          if (response.data && response.data.thread) {
            if (response.data.thread.status === 'done') {
              this.loadObjectFull(index, this.workItems[index].objectId)
            } else {
              this.delayThreadRemoveAttr(threadName, index)
            }
          }
        })
        .catch(e => {
          console.error('Failed to load thread remove attr:', e)
        })
    },
    toggleItemAttrs(index) {
      const item = this.workItems[index]
      this.$set(this.workItems, index, { ...item, attrsExpanded: !item.attrsExpanded })
    },
    loadObjectFull(index, objectId) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'objectfull',
          mode: 'sync',
          param: { objectid: objectId },
        },
      })
        .then(response => {
          const item = this.workItems[index]
          if (!item || item.objectId !== objectId) return
          let attrs = []
          let objectFull = null
          if (response.data.result && response.data.result.objectfull) {
            objectFull = response.data.result.objectfull
            attrs = (objectFull.attributes || []).map(a => ({
              id: a.entity ? a.entity.id : a.id,
              entityName: a.entity ? a.entity.name : '',
              entitySid: a.entity ? a.entity.sid : (a.entitysid || ''),
              datatype: a.datatype ? a.datatype.name : '',
            }))
          }
          const shouldFocus = item.pendingFocusAttr
          this.$set(this.workItems, index, {
            ...item, objectFull, attributes: attrs, attrsLoading: false, pendingFocusAttr: false,
          })
          if (shouldFocus && attrs.length > 0) {
            this.activeWorkItemIndex = index
            this.$nextTick(() => {
              const card = this.$refs[`objCard_${index}`]
              const el = Array.isArray(card) ? card[0] : card
              if (el && el.focusFirstAttr) el.focusFirstAttr()
            })
          }
          // load del status for attributes from the attribute object
          if (objectFull && objectFull.attributes && objectFull.attributes.length > 0) {
            this.loadAttrDelStatus(index, objectId)
          }
        })
        .catch(() => {
          const item = this.workItems[index]
          if (item) {
            this.$set(this.workItems, index, {
              ...item, objectFull: null, attributes: [], attrsLoading: false,
            })
          }
        })
    },
    loadMountSteps({ objectId, callback }) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'query',
          mode: 'sync',
          param: {
            objectsid: 'mountstep',
            limit: 1000,
            offset: 0,
            filter: {
              node: {
                isactive: true,
                oper: { sid: 'and' },
                nodes: [],
                conds: [{
                  isactive: true,
                  oper: { sid: 'equal' },
                  args: [
                    { type: { sid: 'entitysid' }, value: 'object' },
                    { type: { sid: 'value' }, value: String(objectId) },
                  ],
                }],
              },
            },
          },
        },
      })
        .then(response => {
          if (response.data.result && response.data.result.query) {
            const { records } = response.data.result.query.recordset
            const steps = (records || []).map(rec => {
              const c = rec.cells || rec
              return {
                id: c.id ? c.id.value : 0,
                del: !!(c.del && c.del.value),
                name: c.name ? c.name.value : '',
                ordernum: c.ordernum ? c.ordernum.value : 0,
                operId: c.mountoperid ? c.mountoperid.value : null,
                oper: c.mountoperid ? (c.mountoperid.title || c.mountoperid.value) : '',
                destobjectId: c.destobjectid ? c.destobjectid.value : null,
                destobject: c.destobjectid ? (c.destobjectid.title || c.destobjectid.value) : '',
                destscenario: c.destscenarioid ? (c.destscenarioid.title || c.destscenarioid.value) : '',
                sourceobjectId: c.sourceobjectid ? c.sourceobjectid.value : null,
                sourceobject: c.sourceobjectid ? (c.sourceobjectid.title || c.sourceobjectid.value) : '',
                sourcescenario: c.sourcescenarioid ? (c.sourcescenarioid.title || c.sourcescenarioid.value) : '',
                descr: c.descr ? c.descr.value : '',
                jsonfilter: c.jsonfilter ? c.jsonfilter.value : '',
                jsonmod: c.jsonmod ? c.jsonmod.value : '',
                jsongroupby: c.jsongroupby ? c.jsongroupby.value : '',
                jsonjoin: c.jsonjoin ? c.jsonjoin.value : '',
                _raw: rec,
              }
            }).sort((a, b) => a.ordernum - b.ordernum)
            callback(steps)
          } else {
            callback([])
          }
        })
        .catch(() => {
          callback([])
        })
    },
    loadMountOperands() {
      if (this.mountOperands.length > 0) return
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'query',
          mode: 'sync',
          param: {
            objectsid: 'mountoperand',
            limit: 100,
            offset: 0,
          },
        },
      })
        .then(response => {
          if (response.data.result && response.data.result.query) {
            const { records } = response.data.result.query.recordset
            this.mountOperands = (records || []).map(rec => {
              const c = rec.cells || rec
              return {
                id: c.id ? c.id.value : 0,
                sid: c.sid ? c.sid.value : '',
                name: c.name ? c.name.value : '',
              }
            }).filter(o => o.id)
          }
        })
        .catch(() => {})
    },
    async loadObjectFields(objectId) {
      if (!objectId || this.objectFieldsCache[objectId]) return
      this.$set(this.objectFieldsCache, objectId, { loading: true, fields: [] })
      const objectfull = await useCubus.loadObject(this, { objectid: objectId })
      const fields = objectfull
        ? (objectfull.attributes || []).map(a => ({
          title: a.label || (a.entity ? a.entity.name : ''),
          value: a.entityid || (a.entity ? a.entity.id : 0),
          datatype: a.datatype && typeof a.datatype === 'object' ? (a.datatype.sid || a.datatype.name || '') : (a.datatype || ''),
          attributeid: a.attributeid || a.id,
          entitysid: a.entitysid || (a.entity ? a.entity.sid : ''),
          parententityid: a.parententityid || 0,
          refobjectid: a.refobjectid || 0,
        }))
        : []
      this.$set(this.objectFieldsCache, objectId, { loading: false, fields })
    },
    toggleDeleteStep({
      stepId, method, raw, callback,
    }) {
      const rec = raw || { id: { value: stepId } }
      const cells = rec.cells || rec
      const newDel = method === 'deleterecord'
      if (cells.del) cells.del.value = newDel
      if (rec.cells && rec.cells.del) rec.cells.del.value = newDel
      rec.status = 'changed'
      const mountStepObj = this.allObjects.find(o => o.sid === 'mountstep')
      const param = { record: rec }
      if (mountStepObj) param.objectid = mountStepObj.id
      param.objectsid = 'mountstep'
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method,
          param,
        },
      })
        .then(() => {
          if (callback) callback(true)
        })
        .catch(e => {
          console.error('Failed to toggle delete step:', e)
          if (callback) callback(false)
        })
    },
    updateStepField(index, {
      stepId, field, value, callback,
    }) {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'saverecord',
          mode: 'sync',
          param: {
            objectsid: 'mountstep',
            record: {
              id: { value: stepId, status: 'loaded' },
              [field]: { value, status: 'changed' },
            },
          },
        },
      })
        .then(response => {
          if (response.data.error) {
            if (callback) callback(false)
          } else if (callback) callback(true)
        })
        .catch(e => {
          console.error('Failed to update step:', e)
          if (callback) callback(false)
        })
    },
    async renameObject(index, name) {
      const item = this.workItems[index]
      if (!item || !item.objectId) return
      const oldName = item.command
      this.$set(item, 'command', name)
      const obj = this.allObjects.find(o => o.id === item.objectId)
      if (obj) obj.name = name
      const saved = await useCubus.saveRecord(this, {
        objectsid: 'object',
        record: {
          id: { value: item.objectId, status: 'loaded' },
          name: { value: name, status: 'changed' },
        },
      })
      if (!saved) {
        this.$set(item, 'command', oldName)
        if (obj) obj.name = oldName
      }
    },
    activateWorkItem(index) {
      this.activeWorkItemIndex = index
      this.focusedTreeIndex = -1
    },
    async addMountStep(workItemIndex) {
      const item = this.workItems[workItemIndex]
      if (!item || !item.objectId) return
      console.log('item', item)
      const result = await useCubus.loadNewRecord(this, { objectsid: 'mountstep' })
      if (!result || !result.record) return
      const rec = result.record.record
      const cols = result.record.columns || []
      const findKey = sid => {
        const col = cols.find(c => c.entitysid === sid)
        return col ? col.key : sid
      }
      // const objKey = findKey('object')
      const objKey = 'object'
      console.log('objKey', objKey)
      const nameKey = 'name'
      const ordernumKey = 'ordernum'
      if (rec[objKey]) { rec[objKey].value = item.objectId; rec[objKey].status = 'changed' }
      if (rec[nameKey]) { rec[nameKey].value = 'New Step'; rec[nameKey].status = 'changed' }
      const card = this.$refs[`objCard_${workItemIndex}`]
      const cardEl = Array.isArray(card) ? card[0] : card
      const existingSteps = (cardEl && cardEl.steps) || []
      const maxOrder = existingSteps.reduce((max, s) => Math.max(max, s.ordernum || 0), 0)
      const nextOrder = Math.ceil((maxOrder + 1) / 100) * 100
      if (rec[ordernumKey]) { rec[ordernumKey].value = nextOrder; rec[ordernumKey].status = 'changed' }
      console.log('before save', rec)
      await useCubus.saveRecord(this, { objectsid: 'mountstep', keymode: 'sid', record: rec })
      this.reloadStepsForCard(workItemIndex)
    },
    reloadStepsForCard(workItemIndex) {
      const card = this.$refs[`objCard_${workItemIndex}`]
      const el = Array.isArray(card) ? card[0] : card
      if (el && el.reloadSteps) el.reloadSteps()
    },
    toggleItemExpand(index) {
      const item = this.workItems[index]
      this.$set(this.workItems, index, { ...item, expanded: !item.expanded })
    },
    toggleCollapse(index) {
      const item = this.workItems[index]
      this.$set(this.workItems, index, { ...item, collapsed: !item.collapsed })
    },
    removeWorkItem(index, item) {
      this.workItems.splice(index, 1)
      if (item.objectId) {
        this.$delete(this.selectedObjects, item.objectId)
      }
      if (item.joinId) {
        this.$delete(this.selectedJoins, item.joinId)
      }
      if (item.formulaId) {
        this.$delete(this.selectedFormulas, item.formulaId)
      }
    },
    onObjectSelected(ctx) {
      const { objectId } = ctx
      if (this.selectedObjects[objectId]) return
      const existing = this.allObjects.find(o => o.id === objectId)
      const obj = existing || {
        id: objectId, name: ctx.objectName || '', sid: '', meta: '', meta_name: '',
      }
      this.toggleObjectSelection(obj)
    },
    onObjectCreated(ctx) {
      const { objectId } = ctx
      const objectName = ctx.objectName || ''
      if (this.allObjects.some(o => o.id === objectId)) return

      // extract metasid from pendingCommand: "/create <metasid> <name>"
      const parts = (this.pendingCommand || '').split(/\s+/)
      const metasid = parts.length >= 2 ? parts[1] : 'table'

      const metaNames = {
        table: 'Table',
        catalog: 'Catalog',
        cube: 'Cube',
        form: 'Form',
        report: 'Report',
        procedure: 'Procedure',
        import: 'Import',
      }

      const obj = {
        id: objectId,
        name: objectName,
        sid: '',
        meta: metasid,
        meta_name: metaNames[metasid] || metasid,
      }

      this.allObjects.push(obj)
      this.toggleObjectSelection(obj)
      if (metasid === 'catalog') this.loadAllEntities()
    },
    addWorkObject(objectId) {
      if (this.selectedObjects[objectId]) return
      const obj = this.allObjects.find(o => o.id === objectId)
      if (obj) {
        this.toggleObjectSelection(obj)
      }
    },
    toggleJoins() {
      this.joinsExpanded = !this.joinsExpanded
      if (this.joinsExpanded && this.joins.length === 0 && !this.joinsLoading) {
        this.loadJoins()
      }
    },
    async loadJoins() {
      this.joinsLoading = true
      try {
        const query = await useCubus.loadQuery(this, {
          keymode: 'sid',
          objectsid: 'cbsjoin',
          limit: 10000,
          offset: 0,
        })
        if (query && query.recordset) {
          const { records } = query.recordset
          this.joins = (records || []).map(rec => {
            const c = rec.cells || rec
            return {
              id: c.id ? c.id.value : 0,
              sid: c.sid ? c.sid.value : '',
              name: c.name ? c.name.value : '',
              descr: c.descr ? c.descr.value : '',
              del: !!(c.del && c.del.value),
              sourceobject: c.sourceobject ? c.sourceobject : null,
              jsonfilter: c.jsonfilter ? c.jsonfilter.value : null,
            }
          }).filter(j => j.id && !j.del)
        }
      } finally {
        this.joinsLoading = false
      }
    },
    toggleFormulas() {
      this.formulasExpanded = !this.formulasExpanded
      if (this.formulasExpanded && this.formulas.length === 0 && !this.formulasLoading) {
        this.loadFormulas()
      }
    },
    async loadFormulas() {
      this.formulasLoading = true
      try {
        const query = await useCubus.loadQuery(this, {
          keymode: 'sid',
          objectsid: 'formula',
          limit: 10000,
          offset: 0,
        })
        if (query && query.recordset) {
          const { records } = query.recordset
          this.formulas = (records || []).map(rec => {
            const c = rec.cells || rec
            return {
              id: c.id ? c.id.value : 0,
              sid: c.sid ? c.sid.value : '',
              name: c.name ? c.name.value : '',
              descr: c.descr ? c.descr.value : '',
              del: !!(c.del && c.del.value),
            }
          }).filter(f => f.id && !f.del)
        }
      } finally {
        this.formulasLoading = false
      }
    },
    async createJoin(name) {
      // Ensure joins are loaded for duplicate check
      if (this.joins.length === 0 && !this.joinsLoading) {
        await this.loadJoins()
      } else if (this.joinsLoading) {
        this.showStatus('Подождите, идёт загрузка связок...')
        return
      }

      // Duplicate check
      const nameLower = name.toLowerCase()
      const duplicate = this.joins.find(j => (j.name || '').toLowerCase() === nameLower)
      if (duplicate) {
        this.showStatus(`Связка с именем **"${name}"** уже существует.`)
        return
      }

      this.showStatus(`Создаю связку "${name}"...`)
      try {
        const result = await useCubus.loadNewRecord(this, { objectsid: 'cbsjoin' })
        if (!result || !result.record) throw new Error('Не удалось загрузить шаблон записи')
        const rec = result.record.record
        if (rec.name) { rec.name.value = name; rec.name.status = 'changed' }
        await useCubus.saveRecord(this, { objectsid: 'cbsjoin', keymode: 'sid', record: rec })

        // Reload joins to include the new one
        await this.loadJoins()

        // Expand joins folder and open new join
        this.joinsExpanded = true
        const newJoin = this.joins.find(j => (j.name || '').toLowerCase() === nameLower)
        if (newJoin && !this.isJoinSelected(newJoin.id)) {
          this.toggleJoinSelection(newJoin)
        }
        this.showStatus(`Связка **"${name}"** создана.`)
      } catch (e) {
        this.showStatus(`Ошибка: ${e.message || e}`)
      }
    },
    isJoinSelected(joinId) {
      return !!this.selectedJoins[joinId]
    },
    toggleJoinSelection(join) {
      if (this.selectedJoins[join.id]) {
        this.$delete(this.selectedJoins, join.id)
        this.workItems = this.workItems.filter(item => item.joinId !== join.id)
      } else {
        this.$set(this.selectedJoins, join.id, join)
        this.workItems.push({
          joinId: join.id,
          command: join.name || join.sid || `#${join.id}`,
          joinName: join.name || '',
          joinSid: join.sid || '',
          joinDescr: join.descr || '',
          sourceobjectId: join.sourceobjectId || null,
          sourceobject: join.sourceobject || '',
          jsonfilter: join.jsonfilter || '',
          collapsed: false,
        })
        this.activeWorkItemIndex = this.workItems.length - 1
        this.scrollToBottom()
      }
    },
    isFormulaSelected(formulaId) {
      return !!this.selectedFormulas[formulaId]
    },
    toggleFormulaSelection(formula) {
      if (this.selectedFormulas[formula.id]) {
        this.$delete(this.selectedFormulas, formula.id)
        this.workItems = this.workItems.filter(item => item.formulaId !== formula.id)
      } else {
        this.$set(this.selectedFormulas, formula.id, formula)
        this.workItems.push({
          formulaId: formula.id,
          command: formula.name || formula.sid || `#${formula.id}`,
          formulaName: formula.name || '',
          formulaSid: formula.sid || '',
          formulaDescr: formula.descr || '',
          collapsed: false,
        })
        this.activeWorkItemIndex = this.workItems.length - 1
        this.scrollToBottom()
      }
    },
    async updateJoinField(index, { field, value, title }) {
      const item = this.workItems[index]
      if (!item || !item.joinId) return
      const params = {
        objectsid: 'cbsjoin',
        keymode: 'sid',
        record: {
          id: { value: item.joinId, status: 'loaded' },
          [field]: { value, title, status: 'changed' },
        },
      }
      console.log('Updating join field with params:', params)
      const saved = await useCubus.saveRecord(this, params)
      if (saved) {
        this.$set(item, field, { value, title })
        if (field === 'name') this.$set(item, 'command', value || item.joinSid || `#${item.joinId}`)
        const join = this.joins.find(j => j.id === item.joinId)
        if (join) join[field] = { value, title }
      }
    },
    async updateFormulaField(index, { field, value, itemField }) {
      const item = this.workItems[index]
      if (!item || !item.formulaId) return
      const saved = await useCubus.saveRecord(this, {
        objectsid: 'formula',
        keymode: 'sid',
        record: {
          id: { value: item.formulaId, status: 'loaded' },
          [field]: { value, status: 'changed' },
        },
      })
      if (saved) {
        this.$set(item, itemField, value)
        if (field === 'name') this.$set(item, 'command', value || item.formulaSid || `#${item.formulaId}`)
        const formula = this.formulas.find(f => f.id === item.formulaId)
        if (formula) formula[field] = value
      }
    },
    toggleObjectSelection(obj) {
      if (this.selectedObjects[obj.id]) {
        this.$delete(this.selectedObjects, obj.id)
        this.workItems = this.workItems.filter(item => item.objectId !== obj.id)
      } else {
        this.$set(this.selectedObjects, obj.id, obj)
        const itemIndex = this.workItems.length
        this.workItems.push({
          objectId: obj.id,
          command: obj.name,
          sid: obj.sid,
          metaName: obj.meta_name || (obj.meta ? obj.meta.name : ''),
          expanded: true,
          attrsLoading: true,
          pendingFocusAttr: true,
        })
        this.activeWorkItemIndex = itemIndex
        this.loadObjectFull(itemIndex, obj.id)
        this.scrollToBottom()
      }
    },
    // ---- Chain Wizard methods ----
    openChainWizard() {
      this.ensureEntitiesLoaded()
      this.loadMountOperands()
      this.editChainId = null
      this.chainWizardVisible = true
    },
    onChainCreated(objectIds) {
      this.editChainId = null
      this.loadAllObjects()
      // Add created objects to workspace after tree reloads
      const waitAndAdd = () => {
        if (this.treeLoading) {
          setTimeout(waitAndAdd, 300)
          return
        }
        objectIds.forEach(id => {
          if (!this.selectedObjects[id]) {
            const obj = this.allObjects.find(o => o.id === id)
            if (obj) this.toggleObjectSelection(obj)
          }
        })
      }
      setTimeout(waitAndAdd, 500)
    },
    onChainUpdated(objectIds) {
      this.editChainId = null
      this.loadAllObjects()
      // Refresh any open work items that belong to the chain
      const waitAndRefresh = () => {
        if (this.treeLoading) {
          setTimeout(waitAndRefresh, 300)
          return
        }
        this.workItems.forEach((item, index) => {
          if (item.objectId && objectIds.includes(item.objectId)) {
            this.loadObjectFull(index, item.objectId)
          }
        })
      }
      setTimeout(waitAndRefresh, 500)
      this.showStatus('Цепочка обновлена.')
    },
    openChainWizardForEdit(chainId) {
      this.ensureEntitiesLoaded()
      this.loadMountOperands()
      this.editChainId = chainId
      this.chainWizardVisible = true
    },
    // ---- Export methods ----
    async loadEntRefSync(entitysid) {
      const token = useJwt.getCubusToken()
      try {
        const resp = await useJwt.query({
          token,
          query: { method: 'entref', mode: 'sync', param: { entityid: 0, entitysid } },
        })
        if (resp.data.result && resp.data.result.ref) {
          return resp.data.result.ref.map(x => ({ value: x.value, sid: x.sid, title: x.title || `(${x.value})` }))
        }
      } catch (e) {
        console.warn(`Failed to load entref for ${entitysid}:`, e)
      }
      return []
    },
    resolvePropertyRefs(properties, refMaps) {
      if (!properties || !properties.length) return properties
      return properties.map(p => {
        if (!p.argument) return p
        const resolved = { ...p, argument: { ...p.argument } }
        const typeSid = p.argument.argtype && p.argument.argtype.sid
        if (typeSid && refMaps[typeSid] && p.argument.argvalue) {
          const av = p.argument.argvalue
          const refId = av.value || av.id
          if (refId) {
            const found = refMaps[typeSid].find(r => r.value === refId)
            if (found) {
              resolved.argument.argvalue = { ...av, sid: found.sid || av.sid, title: found.title || av.title }
            }
          }
        }
        return resolved
      })
    },
    collectUsedRefs(objectMap) {
      const used = {
        formula: {}, function: {}, scalar: {}, entity: {},
      }
      Object.values(objectMap).forEach(obj => {
        (obj.attributes || []).forEach(attr => {
          (attr.properties || []).forEach(p => {
            if (!p.argument || !p.argument.argvalue) return
            const typeSid = p.argument.argtype && p.argument.argtype.sid
            const av = p.argument.argvalue
            const refId = av.value || av.id
            if (typeSid && used[typeSid] && refId) {
              used[typeSid][refId] = { value: refId, sid: av.sid || null, title: av.title || null }
            }
          })
        })
      })
      const result = {}
      Object.keys(used).forEach(k => {
        const items = Object.values(used[k])
        if (items.length) result[k] = items
      })
      return Object.keys(result).length ? result : null
    },
    async runExport() {
      const rootItems = this.workItems.filter(item => item.objectId)
      if (rootItems.length === 0) {
        this.showStatus('Нет выбранных объектов для экспорта. Используйте `/obj` для добавления.')
        return
      }

      this.exportLoading = true
      this.exportModalVisible = true
      this.exportJson = ''
      this.exportCopied = false

      try {
        // Pre-load reference lists for formulas, functions, scalars
        const [formulaRef, functionRef, scalarRef, entityRef] = await Promise.all([
          this.loadEntRefSync('formula'),
          this.loadEntRefSync('function'),
          this.loadEntRefSync('scalar'),
          this.loadEntRefSync('entity'),
        ])
        const refMaps = {
          formula: formulaRef,
          function: functionRef,
          scalar: scalarRef,
          entity: entityRef,
        }

        const visited = new Set()
        const objectMap = {}
        const queue = rootItems.map(item => item.objectId)

        while (queue.length > 0) {
          const objectId = queue.shift()
          if (visited.has(objectId)) continue
          visited.add(objectId)

          // eslint-disable-next-line no-await-in-loop
          const exported = await this.exportSingleObject(objectId, refMaps)
          objectMap[objectId] = exported

          if (exported.steps && exported.steps.length > 0) {
            exported.steps.forEach(step => {
              if (step.sourceobjectId && !visited.has(step.sourceobjectId)) {
                queue.push(step.sourceobjectId)
              }
              if (step.destobjectId && !visited.has(step.destobjectId)) {
                queue.push(step.destobjectId)
              }
            })
          }
        }

        // Collect all referenced formulas/functions/scalars used across objects
        const usedRefs = this.collectUsedRefs(objectMap)

        const output = {
          exportedAt: new Date().toISOString(),
          rootObjects: rootItems.map(i => i.objectId),
          objects: objectMap,
        }
        if (usedRefs) {
          output.references = usedRefs
        }

        this.exportJson = JSON.stringify(output, null, 2)
      } catch (e) {
        console.error('Export failed:', e)
        this.exportJson = JSON.stringify({ error: 'Export failed', message: String(e) }, null, 2)
      } finally {
        this.exportLoading = false
      }
    },
    async exportSingleObject(objectId, refMaps) {
      const token = useJwt.getCubusToken()

      // Load objectfull (reuse cache from workItems if available)
      let objectFull = null
      const cachedItem = this.workItems.find(i => i.objectId === objectId && i.objectFull)
      if (cachedItem) {
        objectFull = cachedItem.objectFull
      } else {
        try {
          const resp = await useJwt.query({
            token,
            query: { method: 'objectfull', mode: 'sync', param: { objectid: objectId } },
          })
          if (resp.data.result && resp.data.result.objectfull) {
            objectFull = resp.data.result.objectfull
          }
        } catch (e) {
          console.error(`Failed to load objectfull for ${objectId}:`, e)
        }
      }

      if (!objectFull) {
        const obj = this.allObjects.find(o => o.id === objectId)
        return {
          id: objectId,
          name: obj ? obj.name : '(unknown)',
          sid: obj ? obj.sid : '',
          meta: obj ? obj.meta : '',
          error: 'Failed to load objectfull',
          attributes: [],
          steps: [],
        }
      }

      const obj = objectFull.object || {}

      // Use attributes directly from objectfull (includes properties, key, label, etc.)
      // Resolve formula/function/scalar/entity references in properties
      const attrs = (objectFull.attributes || []).map(a => ({
        attributeid: a.attributeid,
        label: a.label,
        key: a.key,
        entityid: a.entityid,
        entitysid: a.entitysid,
        datatype: a.datatype,
        refobjectid: a.refobjectid,
        parententityid: a.parententityid,
        isrequired: a.isrequired,
        isunique: a.isunique,
        descr: a.descr,
        properties: refMaps ? this.resolvePropertyRefs(a.properties, refMaps) : (a.properties || []),
      }))

      // Load mount steps if mount object
      let steps = []
      if (obj.meta === 'mount') {
        try {
          steps = await this.loadMountStepsAsync(objectId)
        } catch (e) {
          console.error(`Failed to load mount steps for ${objectId}:`, e)
        }
      }

      // Extract object params (form_param, render_type, etc.)
      let params = []
      if (objectFull.params && objectFull.params.params && objectFull.params.params.length > 0) {
        params = objectFull.params.params.map(p => ({
          paramname: p.paramname,
          paramvalue: p.paramvalue || '',
          entityid: p.entityid || 0,
          refobjectid: p.refobjectid || 0,
          jsonstring: p.jsonstring || {},
        }))
      }

      const result = {
        id: objectId,
        name: obj.name || '',
        sid: obj.sid || '',
        meta: obj.meta || '',
        attributes: attrs,
        steps,
      }

      if (params.length > 0) {
        result.params = params
      }

      return result
    },
    loadMountStepsAsync(objectId) {
      return new Promise(resolve => {
        this.loadMountSteps({
          objectId,
          callback: steps => {
            const parsed = (steps || []).map(s => ({
              id: s.id,
              name: s.name,
              ordernum: s.ordernum,
              oper: s.oper,
              operId: s.operId,
              destobjectId: s.destobjectId,
              destobject: s.destobject,
              destscenario: s.destscenario,
              sourceobjectId: s.sourceobjectId,
              sourceobject: s.sourceobject,
              sourcescenario: s.sourcescenario,
              descr: s.descr,
              del: s.del,
              jsonfilter: this.safeJsonParse(s.jsonfilter),
              jsonmod: this.safeJsonParse(s.jsonmod),
              jsongroupby: this.safeJsonParse(s.jsongroupby),
              jsonjoin: this.safeJsonParse(s.jsonjoin),
            }))
            resolve(parsed.filter(st => !st.del))
          },
        })
      })
    },
    safeJsonParse(str) {
      if (!str) return null
      try {
        return typeof str === 'string' ? JSON.parse(str) : str
      } catch {
        return str
      }
    },
    copyExportJson() {
      navigator.clipboard.writeText(this.exportJson).then(() => {
        this.exportCopied = true
        setTimeout(() => { this.exportCopied = false }, 2000)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.constructor-page {
  height: calc(100vh - 160px);
  max-height: calc(100vh - 160px);
  display: flex;
  overflow: hidden;
}

.constructor-layout {
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.constructor-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.constructor-workspace {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: block;

  > * + * {
    margin-top: 12px;
  }
}

.constructor-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 40px;
}

.constructor-work-item {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
}

.constructor-work-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f8f8;
  font-size: 12px;
  font-family: monospace;
  color: #6e6b7b;
  border-bottom: 1px solid #ebe9f1;
}

.constructor-work-item-toggle {
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  color: #b9b9c3;

  &:hover {
    color: #5e5873;
  }
}

.constructor-work-item-cmd {
  font-weight: 600;
  color: #7367f0;
  flex: 1;
}

.constructor-work-item--active > .constructor-work-item-header {
  background: #7367f0;
  color: #fff;
  border-color: #7367f0;

  .constructor-work-item-toggle {
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      color: #fff;
    }
  }

  .constructor-work-item-cmd {
    color: #fff;
  }

  .constructor-work-item-close {
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

.constructor-work-item-close {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;

  &:hover {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.1);
  }
}

.constructor-work-item-body {
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.constructor-markdown ::v-deep {
  p:last-child {
    margin-bottom: 0;
  }

  pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
  }

  code {
    font-size: 13px;
  }

  ul, ol {
    padding-left: 20px;
    margin-bottom: 0.5em;
  }

  table {
    border-collapse: collapse;
    margin: 0.5em 0;

    th, td {
      border: 1px solid #ddd;
      padding: 4px 8px;
    }
  }
}

.constructor-statusbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 20px;
  border-top: 1px solid #ebe9f1;
  background: #f8f8f8;
  font-size: 13px;
  color: #6e6b7b;
}

.constructor-statusbar-icon {
  flex-shrink: 0;
  color: #7367f0;
}

.constructor-statusbar-label {
  font-weight: 500;
}

.constructor-statusbar-object {
  color: #7367f0;
  font-weight: 600;
}

.constructor-statusbar-id {
  color: #b9b9c3;
  font-size: 12px;
}

.constructor-statusbar-separator {
  color: #b9b9c3;
  flex-shrink: 0;
}

.constructor-statusbar-entity {
  color: #28c76f;
  font-weight: 600;
}

.constructor-status-message {
  padding: 4px 12px;
  font-size: 12px;
  color: #5e5873;
  background: #f0f0f5;
  border-top: 1px solid #ebe9f1;
  cursor: pointer;
  line-height: 1.4;
  max-height: 160px;
  overflow-y: auto;
  flex-shrink: 0;

  &:hover {
    background: #e8e8ed;
  }
}

.constructor-input-area {
  padding: 16px 20px;
  border-top: 1px solid #ebe9f1;
}

.constructor-form {
  display: flex;
  gap: 8px;
}

.constructor-divider {
  width: 4px;
  cursor: col-resize;
  background: #ebe9f1;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: #7367f0;
  }
}

.constructor-props {
  padding: 16px;
  overflow-y: auto;
}

.constructor-props-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #5e5873;
}

.constructor-props-section {
  margin-bottom: 8px;
}

.constructor-props-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #b9b9c3;
  letter-spacing: 0.5px;
}

.constructor-props-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  font-size: 13px;
  border-bottom: 1px solid #f3f2f5;
}

.constructor-props-key {
  color: #6e6b7b;
  font-weight: 500;
}

.constructor-props-val {
  color: #5e5873;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.constructor-tree {
  font-size: 13px;
}

.constructor-tree-search {
  font-size: 12px;
  height: 26px;
  padding: 0 8px;
  margin: 4px 0;
  width: calc(100% - 8px);
  box-sizing: border-box;
}

.constructor-tree-root {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 0;
  margin-bottom: 2px;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-tree-icon {
  color: #7367f0;
  flex-shrink: 0;
}

.constructor-tree-domain {
  font-weight: 600;
  color: #5e5873;
}

.constructor-tree-loading {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.constructor-tree-meta {
  margin-top: 2px;
  padding-left: 18px;
}

.constructor-tree-meta-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 0;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-tree-toggle {
  color: #b9b9c3;
  flex-shrink: 0;
}

.constructor-tree-meta-name {
  font-weight: 500;
  color: #5e5873;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.constructor-tree-meta-count {
  font-size: 11px;
  color: #b9b9c3;
  flex-shrink: 0;
}

.constructor-tree-objects {
  padding-left: 18px;
}

.constructor-tree-object {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 0;
}

.constructor-tree-object-name {
  color: #6e6b7b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.constructor-tree-focused {
  background: rgba(115, 103, 240, 0.08);
  outline: 1px solid rgba(115, 103, 240, 0.3);
  border-radius: 4px;
}

.constructor-tree:focus {
  outline: none;
}

// Dark mode
.dark-layout {
  .constructor-layout {
    background: #283046;
  }

  .constructor-work-item {
    border-color: #3b4253;
  }

  .constructor-work-item-header {
    background: #242b3d;
    border-color: #3b4253;
    color: #b4b7bd;
  }

  .constructor-work-item-body {
    color: #d0d2d6;
  }

  .constructor-statusbar,
  .constructor-input-area {
    border-color: #3b4253;
  }

  .constructor-statusbar {
    background: #242b3d;
    color: #b4b7bd;
  }

  .constructor-divider {
    background: #3b4253;
  }

  .constructor-props {
    border-color: #3b4253;
  }

  .constructor-props-title {
    color: #d0d2d6;
  }

  .constructor-props-row {
    border-color: #3b4253;
  }

  .constructor-props-key {
    color: #b4b7bd;
  }

  .constructor-props-val {
    color: #d0d2d6;
  }

  .constructor-tree-root {
    border-color: #3b4253;
  }

  .constructor-tree-domain {
    color: #d0d2d6;
  }

  .constructor-tree-root:hover,
  .constructor-tree-meta-header:hover {
    background: #3b4253;
  }

  .constructor-tree-meta-name {
    color: #d0d2d6;
  }

  .constructor-tree-object-name {
    color: #b4b7bd;
  }

  .constructor-tree-focused {
    background: rgba(115, 103, 240, 0.15);
    outline-color: rgba(115, 103, 240, 0.4);
  }
}
</style>
